import { Scene } from 'phaser';
import { UnitType } from '../config/BalanceConfig';
import { Unit } from '../entities/Unit';
import { Enemy } from '../entities/Enemy';
import { Base } from '../entities/Base';
import { CombatSystem } from '../systems/CombatSystem';
import { SpawnSystem } from '../systems/SpawnSystem';
import { ProgressionSystem } from '../systems/ProgressionSystem';
import { WaveSystem } from '../systems/WaveSystem';
import { UnitAssignmentUI } from '../../ui/UnitAssignmentUI';
import { ChargerStorageUI } from '../../ui/ChargerStorageUI';
import { GameInfoUI } from '../../ui/GameInfoUI';
import { POSITIONS, SIZES, TIMING } from '../config/Constants';

export class GameScene extends Scene {
  private base!: Base;
  private combatSystem!: CombatSystem;
  private spawnSystem!: SpawnSystem;
  private progressionSystem!: ProgressionSystem;
  private waveSystem!: WaveSystem;
  private unitAssignmentUI!: UnitAssignmentUI;
  private chargerStorageUI!: ChargerStorageUI;
  private gameInfoUI!: GameInfoUI;
  private gameStartTime: number = 0;
  private backgroundMusic!: Phaser.Sound.BaseSound;

  constructor() {
    super({ key: 'GameScene' });
  }

  create(): void {
    // Handle resize
    this.scale.on('resize', this.handleResize, this);

    this.setupGame();
    this.setupEventListeners();
    this.startBackgroundMusic();
    this.startGame();
    this.gameStartTime = Date.now();
  }

  private setupGame(): void {
    const { height } = this.cameras.main;
    const baseLine = height - POSITIONS.BASE_LINE_OFFSET;

    // Initialize systems
    this.base = new Base(this, baseLine, SIZES.BASE_WIDTH);
    this.combatSystem = new CombatSystem(this);
    this.spawnSystem = new SpawnSystem(this);
    this.progressionSystem = new ProgressionSystem(this);
    this.waveSystem = new WaveSystem(this);

    // Initialize UI components
    this.unitAssignmentUI = new UnitAssignmentUI(this);
    this.chargerStorageUI = new ChargerStorageUI(this);
    this.gameInfoUI = new GameInfoUI(this);
    
    // Initialize available units display
    this.gameInfoUI.setAvailableUnits(this.progressionSystem.getAvailableUnits());
  }

  private handleResize(): void {
    // Preserve current state before recreating UI
    const availableUnits = this.unitAssignmentUI ? this.progressionSystem.getAvailableUnits() : 0;
    const unitCounts = new Map<UnitType, number>();
    
    if (this.unitAssignmentUI) {
      unitCounts.set(UnitType.TANK, this.unitAssignmentUI.getUnitCount(UnitType.TANK));
      unitCounts.set(UnitType.RANGED, this.unitAssignmentUI.getUnitCount(UnitType.RANGED));
      unitCounts.set(UnitType.CHARGER, this.unitAssignmentUI.getUnitCount(UnitType.CHARGER));
    }

    // Recreate UI elements with new dimensions
    if (this.unitAssignmentUI) this.unitAssignmentUI.destroy();
    if (this.chargerStorageUI) this.chargerStorageUI.destroy();
    if (this.gameInfoUI) this.gameInfoUI.destroy();

    this.unitAssignmentUI = new UnitAssignmentUI(this);
    this.chargerStorageUI = new ChargerStorageUI(this);
    this.gameInfoUI = new GameInfoUI(this);

    // Restore state
    this.unitAssignmentUI.setAvailableUnits(availableUnits);
    unitCounts.forEach((count, unitType) => {
      this.unitAssignmentUI.updateUnitCount(unitType, count);
    });
  }

  update(): void {
    this.combatSystem.update();
  }

  private setupEventListeners(): void {
    // Add global input prevention for better mobile experience
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Prevent default touch behaviors
      if (pointer.isDown) {
        pointer.event.preventDefault();
      }
    });

    // Unit assignment events
    this.events.on('assign-unit', (unitType: UnitType) => {
      this.deployUnit(unitType);
    });

    // Charger storage events
    this.events.on('release-chargers', () => {
      this.releaseChargers();
      this.combatSystem.releaseChargers();
    });

    // Progression events
    this.events.on('unit-available', () => {
      this.unitAssignmentUI.setAvailableUnits(this.progressionSystem.getAvailableUnits());
    });

    // Enemy events
    this.events.on('enemy-spawned', (enemy: Enemy) => {
      this.combatSystem.addEnemy(enemy);
      this.spawnSystem.onEnemySpawned();
    });

    this.events.on('enemy-destroyed', (enemy: Enemy) => {
      this.combatSystem.removeEnemy(enemy);
      this.waveSystem.onEnemyDestroyed();
      this.gameInfoUI.addScore(TIMING.SCORE_PER_KILL);
    });

    // Base events
    this.events.on('base-destroyed', () => {
      this.gameOver();
    });

    this.events.on('enemy-reached-base', (enemy: Enemy) => {
      this.base.takeDamage(TIMING.DAMAGE_AMOUNT);
      this.gameInfoUI.setBaseHealth(this.base.getHealth(), this.base.getMaxHealth());
      enemy.destroy(); // Remove enemy when it reaches base
    });
  }

  private startGame(): void {
    this.progressionSystem.start();
    this.spawnSystem.start();
    this.waveSystem.start();
  }

  private deployUnit(unitType: UnitType): void {
    const unit = this.progressionSystem.assignUnitToRole(unitType);
    if (unit) {
      this.combatSystem.addUnit(unit);

      // Update UI to reflect new unit count
      this.updateUnitCount(unitType);

      // Special handling for chargers - give option to store
      if (unitType === UnitType.CHARGER && this.progressionSystem.canStoreCharger()) {
        // For now, auto-deploy - could add UI choice later
      }
    }
  }

  private updateUnitCount(unitType: UnitType): void {
    const currentCount = this.unitAssignmentUI.getUnitCount(unitType) || 0;
    this.unitAssignmentUI.updateUnitCount(unitType, currentCount);
  }

  private releaseChargers(): void {
    const chargerCount = this.progressionSystem.releaseChargers();
    for (let i = 0; i < chargerCount; i++) {
      const { width, height } = this.cameras.main;
      const x = Phaser.Math.Between(POSITIONS.MIN_MARGIN, width - POSITIONS.MIN_MARGIN);
      const y = height - POSITIONS.UNIT_SPAWN_Y_OFFSET;

      const charger = new Unit(this, x, y, UnitType.CHARGER);
      this.combatSystem.addUnit(charger);
    }
  }

  private startBackgroundMusic(): void {
    this.backgroundMusic = this.sound.add('background-music', { 
      loop: true, 
      volume: 0.5 
    });
    this.backgroundMusic.play();
  }

  private gameOver(): void {
    const gameTime = Math.floor((Date.now() - this.gameStartTime) / TIMING.SECONDS_PER_FRAME);
    const score = this.gameInfoUI.getScore();
    const wave = this.waveSystem.getCurrentWave();

    this.progressionSystem.stop();
    this.spawnSystem.stop();
    this.waveSystem.stop();
    this.backgroundMusic.stop();

    this.scene.start('GameOverScene', { score, wave, gameTime });
  }
}
